import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BUSINESS_TYPES } from "@shop-ledger/shared-types";
import { Button, Input } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { MockBadge } from "../../components/MockBadge";
import { StatusBanner } from "../../components/StatusBanner";
import { PLAN_CODES, PLAN_DEFAULTS, type LiveOnboardingChecklist, type OnboardingSubmission, type PlanCode } from "../../api/types";

const STEP_LABELS = ["Business info", "Branch & owner", "Plan & quota", "Review"] as const;

const INITIAL_FORM: OnboardingSubmission = {
  business_name: "",
  business_type: null,
  currency: "UGX",
  branch_name: "Main Branch",
  owner_email: "",
  plan_code: "TRIAL",
  ...PLAN_DEFAULTS.TRIAL,
};

/**
 * §9.2's guided onboarding flow — business info → first branch/owner → plan/quota → a
 * review step that submits. On success, shows the fresh tenant's real, live onboarding
 * checklist and a one-time invite link for the new owner, and links straight to the
 * tenant's detail page.
 *
 * Real vs. mocked, by client (see ARCHITECTURE.md's shape-difference table):
 *  - Steps 1–2 (business info, branch/owner) are genuinely submitted to the real
 *    `POST /api/v1/platform/tenants` by default (`realPlatformApiClient.createTenant`) —
 *    that route calls the SAME `tenant_service.create_tenant` the internal
 *    `/auth/tenants/bootstrap` route already used. A real `Tenant` + `Branch` + `OWNER`
 *    `User` are created. **2026-07-13 fix**: that route no longer collects `owner_password`
 *    at all — it issues a real one-time credential-setup token for the new OWNER instead
 *    (the same invite mechanism `POST /users` uses), returned here as `setup_token` and
 *    shown to the operator below, with an explicit "no real email sending exists yet, hand
 *    this to the owner directly" disclosure lifted verbatim from the backend response.
 *  - Step 3 (plan/quota) has NO backend concept at all — `Tenant` gained plan/quota
 *    *columns* in this same pass, but no platform-facing route reads or writes them yet
 *    (verified directly against `backend/app/schemas/platform.py`). This step's fields are
 *    collected and shown in the review step (so the wizard's UX flow stays intact) but are
 *    never sent to, or persisted by, the real backend — badged accordingly below.
 *  - The post-create onboarding-checklist view calls the REAL `GET
 *    /platform/tenants/{id}/checklist` (`getTenantChecklist`) — computed live from the
 *    tenant's actual branch/product/sale counts, genuinely real in both client modes (the
 *    mock synthesizes the same shape from its own fixture state).
 */
export function OnboardingWizardPage() {
  const client = usePlatformClient();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<OnboardingSubmission>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);
  const [createdChecklist, setCreatedChecklist] = useState<LiveOnboardingChecklist | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  const [inviteNote, setInviteNote] = useState<string | null>(null);
  const [created, setCreated] = useState(false);

  function update<K extends keyof OnboardingSubmission>(key: K, value: OnboardingSubmission[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function applyPlan(plan: PlanCode) {
    setForm((prev) => ({ ...prev, plan_code: plan, ...PLAN_DEFAULTS[plan] }));
  }

  function canAdvanceFrom(currentStep: number): boolean {
    if (currentStep === 0) return form.business_name.trim().length > 0 && form.currency.trim().length === 3;
    if (currentStep === 1) return form.branch_name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.owner_email);
    return true;
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await client.createTenant(form);
      setCreatedTenantId(result.tenant.id);
      setInviteToken(result.setup_token);
      setInviteExpiresAt(result.setup_token_expires_at);
      setInviteNote(result.note);
      try {
        setCreatedChecklist(await client.getTenantChecklist(result.tenant.id));
      } catch {
        setCreatedChecklist(null); // Checklist fetch failing shouldn't hide that the tenant was created for real.
      }
      setCreated(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not create business");
    } finally {
      setSubmitting(false);
    }
  }

  if (createdTenantId && created) {
    return (
      <div className="page">
        <h1 className="page__title">{form.business_name} created</h1>
        <p className="page__subtitle">Business, first branch, and OWNER user created for real.</p>

        <div className="panel invite-panel">
          <h2 className="panel__title">Owner credential-setup invite</h2>
          <p className="muted" style={{ marginBottom: 10 }}>
            {inviteNote}
          </p>
          <div className="invite-panel__token">
            <code>{inviteToken}</code>
          </div>
          {inviteExpiresAt ? (
            <p className="muted" style={{ marginTop: 8 }}>
              Expires {new Date(inviteExpiresAt).toLocaleString()}. Hand this token to{" "}
              <strong>{form.owner_email}</strong> directly (Slack, WhatsApp, in person) — they'll use it at{" "}
              <code>/accept-invite</code> to set their own password. No real email was sent.
            </p>
          ) : null}
        </div>

        <div className="panel">
          <h2 className="panel__title">
            Onboarding checklist{" "}
            <MockBadge live label="live" title="Computed live from this business's real branch/product/sale counts." />
          </h2>
          {createdChecklist ? (
            <ul className="checklist">
              {createdChecklist.steps.map((s) => (
                <li key={s.step} className={`checklist__item ${s.completed ? "checklist__item--done" : ""}`}>
                  <span className="checklist__check">{s.completed ? "✓" : ""}</span>
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {s.completed_at ? (
                    <span className="muted" style={{ fontSize: "0.75rem" }}>
                      {new Date(s.completed_at).toLocaleDateString()}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Could not load the checklist right now — the business was created for real regardless.</p>
          )}
        </div>

        <div className="form-actions">
          <Button type="button" variant="primary" size="md" onClick={() => navigate(`/tenants/${createdTenantId}`)}>
            View business
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => {
              setCreatedTenantId(null);
              setCreatedChecklist(null);
              setInviteToken(null);
              setInviteExpiresAt(null);
              setInviteNote(null);
              setCreated(false);
              setForm(INITIAL_FORM);
              setStep(0);
            }}
          >
            Onboard another business
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page__title">Onboard a new business</h1>
      <p className="page__subtitle">
        Guided flow. Steps 1–2 create a real business/branch/owner and issue a real one-time credential-setup
        invite (no owner password collected here); step 3 (plan/quota) is not persisted yet.
      </p>

      <div className="wizard-steps">
        {STEP_LABELS.map((label, i) => (
          <div key={label} className={`wizard-step ${i === step ? "wizard-step--active" : i < step ? "wizard-step--done" : ""}`}>
            {i + 1}. {label}
          </div>
        ))}
      </div>

      {submitError ? <StatusBanner kind="error">{submitError}</StatusBanner> : null}

      <div className="panel">
        {step === 0 ? (
          <div className="form-grid">
            <Input
              label="Business name"
              value={form.business_name}
              onChange={(e) => update("business_name", e.target.value)}
              required
            />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="business-type">
                Business type
              </label>
              <input
                id="business-type"
                className="sl-input"
                list="business-type-options"
                value={form.business_type ?? ""}
                onChange={(e) => update("business_type", e.target.value || null)}
                placeholder="e.g. RETAIL (free text — see shared-types note)"
              />
              <datalist id="business-type-options">
                {BUSINESS_TYPES.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <Input
              label="Currency (ISO 4217)"
              value={form.currency}
              maxLength={3}
              onChange={(e) => update("currency", e.target.value.toUpperCase())}
              required
            />
          </div>
        ) : null}

        {step === 1 ? (
          <div className="form-grid">
            <Input
              label="First branch name"
              value={form.branch_name}
              onChange={(e) => update("branch_name", e.target.value)}
              required
            />
            <Input
              label="Owner email"
              type="email"
              value={form.owner_email}
              onChange={(e) => update("owner_email", e.target.value)}
              hint="A real one-time credential-setup token is issued for this address on submit — the operator never sets the owner's password."
              required
            />
          </div>
        ) : null}

        {step === 2 ? (
          <div>
            <p className="muted" style={{ marginBottom: 10 }}>
              <MockBadge
                live={false}
                label="not persisted"
                title="Plan/quota isn't enforced yet — these values are shown in the review step but not sent to the backend."
              />{" "}
              These fields are collected for this wizard's flow but have no backend to persist them yet.
            </p>
            <div className="form-grid">
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="plan-code">
                  Plan
                </label>
                <select
                  id="plan-code"
                  className="sl-input"
                  value={form.plan_code}
                  onChange={(e) => applyPlan(e.target.value as PlanCode)}
                >
                  {PLAN_CODES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Storage quota (GB)"
                type="number"
                min={1}
                value={form.storage_quota_gb}
                onChange={(e) => update("storage_quota_gb", Number(e.target.value))}
              />
              <Input
                label="Branch limit"
                type="number"
                min={1}
                value={form.branch_limit}
                onChange={(e) => update("branch_limit", Number(e.target.value))}
              />
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="ecom-enabled">
                  E-commerce enabled
                </label>
                <select
                  id="ecom-enabled"
                  className="sl-input"
                  value={form.ecommerce_enabled ? "yes" : "no"}
                  onChange={(e) => update("ecommerce_enabled", e.target.value === "yes")}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div>
            <h2 className="panel__title">Review</h2>
            <dl>
              <dt>
                <strong>Business</strong>
              </dt>
              <dd>
                {form.business_name} ({form.business_type ?? "unclassified"}), {form.currency}
              </dd>
              <dt>
                <strong>First branch</strong>
              </dt>
              <dd>{form.branch_name}</dd>
              <dt>
                <strong>Owner</strong>
              </dt>
              <dd>{form.owner_email}</dd>
              <dt>
                <strong>Plan</strong>{" "}
                <MockBadge live={false} label="not persisted" title="Plan/quota isn't persisted yet for new businesses." />
              </dt>
              <dd>
                {form.plan_code} — {form.storage_quota_gb} GB, {form.branch_limit} branches,{" "}
                {form.ecommerce_enabled ? "e-commerce enabled" : "e-commerce disabled"}
              </dd>
            </dl>
          </div>
        ) : null}
      </div>

      <div className="form-actions">
        {step > 0 ? (
          <Button type="button" variant="ghost" size="md" onClick={() => setStep((s) => s - 1)} disabled={submitting}>
            Back
          </Button>
        ) : null}
        {step < STEP_LABELS.length - 1 ? (
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={!canAdvanceFrom(step)}
            onClick={() => setStep((s) => s + 1)}
          >
            Next
          </Button>
        ) : (
          <Button type="button" variant="primary" size="md" disabled={submitting} onClick={() => void handleSubmit()}>
            {submitting ? "Creating…" : "Create tenant"}
          </Button>
        )}
      </div>
    </div>
  );
}
